/**
 * Texto editable inline con contentEditable.
 * Soporta formato rico (negrita, cursiva, subrayado) al seleccionar texto.
 */

import { useRef, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bold, Italic, Underline } from 'lucide-react';

interface EditableTextProps {
    value: string;
    onChange: (value: string) => void;
    className?: string;
    tag?: 'div' | 'h1' | 'h2' | 'h3' | 'p' | 'span';
    placeholder?: string;
    style?: React.CSSProperties;
    onFocus?: () => void;
    richFormatting?: boolean;
}

export function EditableText({
    value,
    onChange,
    className,
    tag: Tag = 'div',
    placeholder = 'Escribe aquí...',
    style,
    onFocus,
    richFormatting = false,
}: EditableTextProps) {
    const ref = useRef<HTMLElement | null>(null);
    const [toolbar, setToolbar] = useState<{ top: number; left: number } | null>(null);
    const savedRangeRef = useRef<Range | null>(null);

    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (document.activeElement !== el) {
            el.innerHTML = value ?? '';
        }
    }, [value]);

    useEffect(() => {
        if (!richFormatting || typeof document === 'undefined') return;
        const handleSelection = () => {
            const sel = document.getSelection();
            if (!sel || sel.isCollapsed || !ref.current) {
                setToolbar(null);
                savedRangeRef.current = null;
                return;
            }
            const anchor = sel.anchorNode;
            if (!anchor || !ref.current.contains(anchor)) {
                setToolbar(null);
                savedRangeRef.current = null;
                return;
            }
            try {
                const range = sel.getRangeAt(0).cloneRange();
                savedRangeRef.current = range;
                const rect = range.getBoundingClientRect();
                setToolbar({ top: rect.top - 40, left: rect.left + rect.width / 2 });
            } catch {
                setToolbar(null);
                savedRangeRef.current = null;
            }
        };
        document.addEventListener('selectionchange', handleSelection);
        return () => document.removeEventListener('selectionchange', handleSelection);
    }, [richFormatting]);

    useLayoutEffect(() => {
        if (toolbar && savedRangeRef.current && ref.current) {
            const sel = document.getSelection();
            if (sel) {
                try {
                    sel.removeAllRanges();
                    sel.addRange(savedRangeRef.current);
                } catch {
                    savedRangeRef.current = null;
                }
            }
        }
    }, [toolbar]);

    const execFormat = (cmd: 'bold' | 'italic' | 'underline') => {
        document.execCommand(cmd, false);
        setToolbar(null);
    };

    const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
        const el = e.currentTarget;
        onChange(richFormatting ? (el.innerHTML || '') : (el.textContent ?? ''));
    };

    return (
        <>
            <Tag
                ref={ref as React.RefObject<HTMLParagraphElement>}
                contentEditable
                suppressContentEditableWarning
                onBlur={handleBlur}
                onFocus={onFocus}
                onClick={(e) => e.stopPropagation()}
                className={`outline-none focus:ring-2 focus:ring-primary/40 focus:rounded-sm cursor-text empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground ${className ?? ''}`}
                data-placeholder={placeholder}
                style={style}
                dangerouslySetInnerHTML={{ __html: value ?? '' }}
            />
            {richFormatting &&
                toolbar &&
                typeof document !== 'undefined' &&
                createPortal(
                    <div
                        className="fixed z-[100] flex gap-0.5 rounded-lg border border-border bg-background px-1 py-1 shadow-lg"
                        style={{
                            top: toolbar.top,
                            left: toolbar.left,
                            transform: 'translate(-50%, 0)',
                        }}
                        onMouseDown={(e) => e.preventDefault()}
                    >
                        <button
                            type="button"
                            onClick={() => execFormat('bold')}
                            className="p-1.5 rounded hover:bg-muted"
                            title="Negrita"
                            aria-label="Negrita"
                        >
                            <Bold className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => execFormat('italic')}
                            className="p-1.5 rounded hover:bg-muted"
                            title="Cursiva"
                            aria-label="Cursiva"
                        >
                            <Italic className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => execFormat('underline')}
                            className="p-1.5 rounded hover:bg-muted"
                            title="Subrayado"
                            aria-label="Subrayado"
                        >
                            <Underline className="w-4 h-4" />
                        </button>
                    </div>,
                    document.body
                )}
        </>
    );
}
